import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Tag, Button, Space, Drawer, Modal, Input,
  Empty, Descriptions, Tooltip, Table, message
} from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined,
  SafetyCertificateOutlined, EyeOutlined, ClockCircleOutlined, QrcodeOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { ErpDataGrid, ErpMasterShell } from '../../../../components/common/erp-grid';
import ReportViewerModal from '../../../../components/common/ReportViewerModal';
import useAuth from '../../../../hooks/useAuth';
import useApprovalMfa from '../../../../hooks/useApprovalMfa';
import { breakageApi } from './breakageApi';
import {
  BREAKAGE_STATUS_TABS, statusForTab, statusColor, statusLabel,
  reasonLabel, hasPermission, PERM
} from './breakageHelpers';
import '../production.css';

// ════════════════════════════════════════════════════════════════════
// Glass-i — Internal Breakage → Rework Request · Approvals inbox.
//
// Reworked onto the shared compact ErpDataGrid so it feels of a piece with
// Order Tracking and the rest of the production module (slate rhythm, sticky
// header, toolbar search + export + refresh). A Segmented status filter
// (Pending / Approved / Rejected, with live counts) swaps the tab strip. One
// row per breakage slip; opening a slip (row link, Review action, or
// double-click) slides in a detail drawer to inspect every broken piece before
// deciding. Approving a PENDING slip runs the L1 Approval-MFA step-up (the
// backend then auto-issues the remakes); rejecting needs a reason. Approve /
// Reject show only for users holding the breakage-approve permission.
// ════════════════════════════════════════════════════════════════════

function backendError(err, fallback) {
  return (err && err.response && err.response.data && err.response.data.error && err.response.data.error.message)
    || (err && err.message) || fallback || 'Something went wrong.';
}
function dateTime(v) {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
}
function dateShort(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true });
}
// A comma-list cell that ellipsizes and shows the full set on hover. Accepts
// either an array or a comma-joined string (the inbox API returns woNos /
// departments / reasons as string_agg strings).
function listCell(values) {
  const arr = Array.isArray(values)
    ? values.filter(Boolean)
    : String(values || '').split(',').map((s) => s.trim()).filter(Boolean);
  if (!arr.length) return null;
  const text = arr.join(', ');
  return <Tooltip title={text}><span className="text-slate-700">{text}</span></Tooltip>;
}

// Size formatting — a stored millimetre value trimmed to ≤2 decimals, and its
// inch equivalent (1 in = 25.4 mm), so every breakage piece reads in both units.
function trimDim(n) {
  if (n == null || Number.isNaN(Number(n))) return null;
  return Number(n).toFixed(2).replace(/\.?0+$/, '');
}
function sizeMm(w, h) {
  if (w == null && h == null) return null;
  return `${trimDim(w) || '—'} × ${trimDim(h) || '—'} mm`;
}
function sizeInch(w, h) {
  if (w == null && h == null) return null;
  const wi = w != null ? trimDim(w / 25.4) : null;
  const hi = h != null ? trimDim(h / 25.4) : null;
  return `${wi || '—'} × ${hi || '—'} in`;
}

// Leading lifecycle glyph per inbox tab.
const TAB_ICON = {
  pending:  <ClockCircleOutlined />,
  approved: <CheckCircleOutlined />,
  rejected: <CloseCircleOutlined />
};

export default function BreakageApprovalsPage() {
  const { me } = useAuth();
  const permissions = me && me.permissions;
  const mayDecide = hasPermission(permissions, PERM.APPROVE);
  const mayPrintSticker = hasPermission(permissions, PERM.PRINT);
  const { runWithApproval, ApprovalMfaModalSlot } = useApprovalMfa();

  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  // The slip currently being approved (by id), so only that row's Approve
  // button spins — not every pending row's. null = nothing in flight.
  const [approvingId, setApprovingId] = useState(null);

  // Detail drawer for the selected slip.
  const [detail, setDetail] = useState(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const loadCounts = useCallback(async () => {
    try { setCounts(await breakageApi.counts() || {}); } catch (_e) { /* non-fatal */ }
  }, []);
  const load = useCallback(async (tabKey) => {
    setLoading(true);
    try {
      const data = await breakageApi.list(statusForTab(tabKey));
      setRows((data && data.rows) || []);
    } catch (err) {
      message.error(backendError(err, 'Could not load breakage requests.'));
      setRows([]);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(tab); }, [tab, load]);
  useEffect(() => { loadCounts(); }, [loadCounts]);

  const refresh = useCallback(() => { load(tab); loadCounts(); }, [tab, load, loadCounts]);

  const openDetail = useCallback(async (row) => {
    if (!row || !row.id) return;
    setDrawerOpen(true);
    setDetail(row);
    setDetailBusy(true);
    try {
      setDetail(await breakageApi.get(row.id));
    } catch (err) {
      message.error(backendError(err, 'Could not load the slip.'));
    } finally { setDetailBusy(false); }
  }, []);
  const closeDetail = () => { setDrawerOpen(false); setDetail(null); };

  // ── Report viewer (Jasper HTML → in-app modal) ───────────────────────
  // The slip + sticker routes are auth-gated and the access token lives in
  // memory, so we fetch the Jasper-rendered HTML through axios (Bearer attached)
  // and frame it in a scrollable iframe with a Print button — nothing opens in a
  // raw browser tab (which clipped the wide landscape slip).
  const [report, setReport] = useState({ open: false, title: '', html: null, loading: false, error: null, width: '92vw' });
  const lastReportRef = useRef(null);
  const closeReport = useCallback(() => setReport((r) => ({ ...r, open: false })), []);
  const openReport = useCallback(async ({ title, width, fetcher }) => {
    lastReportRef.current = { title, width, fetcher };
    setReport({ open: true, title, html: null, loading: true, error: null, width: width || '92vw' });
    try {
      const html = await fetcher();
      setReport((r) => ({ ...r, html, loading: false }));
    } catch (err) {
      setReport((r) => ({ ...r, loading: false, error: backendError(err, 'Could not render the report.') }));
    }
  }, []);
  const reloadReport = useCallback(() => {
    if (lastReportRef.current) openReport(lastReportRef.current);
  }, [openReport]);

  const viewSlip = useCallback((row) => {
    if (!row || !row.id) return;
    openReport({ title: `Rework Slip · ${row.slipNo || ''}`.trim(), width: '92vw', fetcher: () => breakageApi.slipHtml(row.id) });
  }, [openReport]);
  const viewSticker = useCallback((barcode) => {
    if (!barcode) return;
    openReport({ title: `Remake Sticker · ${barcode}`, width: 620, fetcher: () => breakageApi.pieceSticker(barcode) });
  }, [openReport]);
  // Batch: every remake sticker on the slip in one printable sheet.
  const viewAllRemakeStickers = useCallback((row) => {
    if (!row || !row.id) return;
    openReport({ title: `Remake Stickers · ${row.slipNo || ''}`.trim(), width: 720, fetcher: () => breakageApi.remakeStickers(row.id) });
  }, [openReport]);

  const approve = useCallback(async (row) => {
    if (!row) return;
    setApprovingId(row.id);
    try {
      const res = await runWithApproval({
        operationKey: 'production.workorder.breakage.approve',
        resourceType: 'work_order_breakage',
        resourceId: row.id,
        operationLabel: 'Approve rework',
        resourceLabel: row.slipNo,
        action: (challengeId) => breakageApi.approve(row.id, challengeId)
      });
      const n = res && Array.isArray(res.remakes) ? res.remakes.length : 0;
      message.success(`Rework approved · ${row.slipNo}${n ? ` · ${n} remake${n === 1 ? '' : 's'} issued` : ''}.`);
      closeDetail();
      refresh();
    } catch (err) {
      if (err && err.name === 'ApprovalMfaCancelled') return;
      message.error(backendError(err, 'Could not approve the rework.'));
    } finally { setApprovingId(null); }
  }, [runWithApproval, refresh]);

  const reject = useCallback((row) => {
    if (!row) return;
    let reasonText = '';
    Modal.confirm({
      title: `Reject breakage slip ${row.slipNo}?`,
      icon: <CloseCircleOutlined style={{ color: '#dc2626' }} />,
      content: (
        <div className="mt-2">
          <div className="text-[12px] text-slate-500 mb-1">Reason (required)</div>
          <Input.TextArea rows={3} maxLength={500} showCount
            placeholder="Why is this slip being rejected?"
            onChange={(e) => { reasonText = e.target.value; }} />
        </div>
      ),
      okText: 'Reject',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        const r = (reasonText || '').trim();
        if (!r) { message.warning('Add a reason before you reject.'); return Promise.reject(); }
        try {
          await breakageApi.reject(row.id, r);
          message.success(`Slip ${row.slipNo} rejected.`);
          closeDetail();
          refresh();
        } catch (err) {
          message.error(backendError(err, 'Could not reject the slip.'));
          return Promise.reject(err);
        }
        return undefined;
      }
    });
  }, [refresh]);

  // A compact hover-reveal action cell: Review (opens drawer) + Print, plus
  // quick Approve / Reject on PENDING rows for users who may decide. Buttons
  // stop propagation so the row's double-click-to-open never double-fires.
  const rowActions = useCallback((row) => {
    const isPending = row && row.status === 'PENDING';
    return (
      <Space size={0} onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
        <Tooltip title="Review slip">
          <Button size="small" type="text" icon={<EyeOutlined />} onClick={() => openDetail(row)} />
        </Tooltip>
        <Tooltip title="View / print slip">
          <Button size="small" type="text" icon={<FileTextOutlined />} onClick={() => viewSlip(row)} />
        </Tooltip>
        {isPending && mayDecide ? (
          <>
            <Tooltip title="Approve">
              <Button size="small" type="text" className="erp-brk-approve" icon={<CheckCircleOutlined />} loading={approvingId === row.id} onClick={() => approve(row)} />
            </Tooltip>
            <Tooltip title="Reject">
              <Button size="small" type="text" danger icon={<CloseCircleOutlined />} onClick={() => reject(row)} />
            </Tooltip>
          </>
        ) : null}
      </Space>
    );
  }, [openDetail, viewSlip, approve, reject, mayDecide, approvingId]);

  const columns = useMemo(() => [
    { title: 'S.NO', key: '__sno__', width: 56, align: 'center', emptyFallback: false,
      render: (_v, _r, idx) => <span className="erp-grid__muted">{idx + 1}</span> },
    { title: 'SLIP NO', dataIndex: 'slipNo', key: 'slipNo', width: 168, fixed: 'left', ellipsis: true,
      sorter: (a, b) => String(a.slipNo || '').localeCompare(String(b.slipNo || '')),
      render: (v, r) => v
        ? <Button type="link" size="small" style={{ padding: 0, height: 'auto', fontWeight: 600 }} onClick={() => openDetail(r)}>{v}</Button>
        : null },
    { title: 'PCS', dataIndex: 'pieceCount', key: 'pieceCount', width: 72, align: 'right',
      sorter: (a, b) => (Number(a.pieceCount) || 0) - (Number(b.pieceCount) || 0),
      render: (v) => <span className="tabular-nums text-slate-700">{v == null ? null : v}</span> },
    { title: 'WORK ORDERS', dataIndex: 'woNos', key: 'woNos', width: 200, ellipsis: true, render: (v) => listCell(v) },
    { title: 'DEPARTMENTS', dataIndex: 'departments', key: 'departments', width: 190, ellipsis: true, render: (v) => listCell(v) },
    { title: 'REASONS', dataIndex: 'reasons', key: 'reasons', ellipsis: true,
      render: (v) => listCell(String(v || '').split(',').map((s) => reasonLabel(s.trim())).filter(Boolean)) },
    { title: 'REQUESTED', dataIndex: 'requestedAt', key: 'requestedAt', width: 150, ellipsis: true,
      sorter: (a, b) => String(a.requestedAt || '').localeCompare(String(b.requestedAt || '')),
      render: (v) => <span className="text-slate-500 whitespace-nowrap">{dateShort(v)}</span> },
    { title: 'STATUS', dataIndex: 'status', key: 'status', width: 116, align: 'center', dataType: 'enum',
      render: (v) => <Tag color={statusColor(v)} className="erp-grid__tag" style={{ marginInlineEnd: 0 }}>{(statusLabel(v) || '').toUpperCase()}</Tag> },
    { title: '', key: 'actions', width: mayDecide ? 148 : 84, align: 'center', fixed: 'right', emptyFallback: false,
      render: (_v, r) => rowActions(r) }
  ], [openDetail, rowActions, mayDecide]);

  const contextMenuActions = useMemo(() => ([
    { key: 'review', label: 'Review slip', icon: <EyeOutlined />, onClick: openDetail },
    { key: 'print', label: 'View / print slip', icon: <FileTextOutlined />, onClick: (r) => viewSlip(r) },
    { key: 'approve', label: 'Approve', icon: <CheckCircleOutlined />, visible: (r) => mayDecide && r.status === 'PENDING', onClick: approve },
    { key: 'reject', label: 'Reject', icon: <CloseCircleOutlined />, danger: true, visible: (r) => mayDecide && r.status === 'PENDING', onClick: reject }
  ]), [openDetail, viewSlip, approve, reject, mayDecide]);

  // Gmail-style inbox tab strip — one text tab per status, coloured underline +
  // count pill on the active tab; Pending keeps a soft tinted count while
  // inactive so a waiting queue still draws the eye.
  const tabStrip = useMemo(() => (
    <div className="erp-brk-tabs" role="tablist" aria-label="Breakage status">
      {BREAKAGE_STATUS_TABS.map((t) => {
        const n = counts[t.status] || 0;
        const active = tab === t.key;
        const variant = active ? '--active' : (n && t.key === 'pending' ? '--soft' : '--muted');
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={active}
            className={`erp-brk-tab erp-brk-tab-${t.status}`}
            onClick={() => setTab(t.key)}
          >
            <span className="erp-brk-tab-icon" aria-hidden="true">{TAB_ICON[t.key]}</span>
            {t.label}
            <span className={`erp-brk-tab-count erp-brk-tab-count${variant}`}>{n}</span>
          </button>
        );
      })}
    </div>
  ), [counts, tab]);

  const emptyText = tab === 'pending' ? 'No breakage slips waiting for approval.'
    : tab === 'approved' ? 'No approved breakage slips.'
    : 'No rejected breakage slips.';

  const pageSizeOptions = useMemo(() => ['20', '50', '100'], []);

  const itemColumns = useMemo(() => [
    {
      title: 'Barcode', key: 'barcode', width: 176,
      render: (_v, r) => (
        <div className="leading-tight">
          <code className="erp-grid__code">{r.barcode}</code>
          {r.paneNo ? <div className="text-[11px] text-slate-500 mt-0.5">Pane {r.paneNo}</div> : null}
        </div>
      )
    },
    {
      title: 'Work Order', key: 'wo', width: 200,
      render: (_v, r) => (
        <div className="leading-tight">
          <div className="font-semibold text-slate-700">{r.woNo || '—'}</div>
          <div className="text-[12px] text-slate-500">{r.customerName || '—'}</div>
        </div>
      )
    },
    {
      title: 'Item Description', dataIndex: 'itemDesc', key: 'itemDesc', width: 220, ellipsis: true,
      render: (v) => v ? <Tooltip title={v}><span className="text-slate-700">{v}</span></Tooltip> : <span className="text-slate-300">—</span>
    },
    {
      title: 'Glass', key: 'glass', width: 140,
      render: (_v, r) => {
        const bits = [r.lineType, r.thicknessMm != null ? `${r.thicknessMm} mm` : null].filter(Boolean);
        return bits.length ? <span className="text-slate-700">{bits.join(' · ')}</span> : <span className="text-slate-300">—</span>;
      }
    },
    {
      title: 'Size (W × H)', key: 'size', width: 156,
      render: (_v, r) => {
        const mm = sizeMm(r.widthMm, r.heightMm);
        if (!mm) return <span className="text-slate-300">—</span>;
        return (
          <div className="leading-tight">
            <div className="text-slate-700 tabular-nums">{mm}</div>
            <div className="text-[12px] text-slate-500 tabular-nums">{sizeInch(r.widthMm, r.heightMm)}</div>
          </div>
        );
      }
    },
    {
      title: 'Fabrication', dataIndex: 'fabInfo', key: 'fabInfo', width: 120, ellipsis: true,
      render: (v) => v ? <Tooltip title={v}><span className="text-slate-700">{v}</span></Tooltip> : <span className="text-slate-300">—</span>
    },
    { title: 'Department', dataIndex: 'departmentCode', key: 'departmentCode', width: 128, render: (v) => v || '—' },
    { title: 'Reason', dataIndex: 'reasonCode', key: 'reasonCode', width: 148, render: (v) => reasonLabel(v) },
    {
      title: 'Remarks', dataIndex: 'lineRemarks', key: 'lineRemarks', width: 180, ellipsis: true,
      render: (v) => v ? <Tooltip title={v}><span className="text-slate-600">{v}</span></Tooltip> : <span className="text-slate-300">—</span>
    },
    { title: 'Remake', dataIndex: 'remakeBarcode', key: 'remakeBarcode', width: 168, render: (v) => v ? <code className="erp-grid__code">{v}</code> : <span className="text-slate-300">—</span> },
    {
      title: 'Sticker', key: 'sticker', width: 104, align: 'center', fixed: 'right',
      render: (_v, r) => {
        if (!r.remakeBarcode) return <Tooltip title="Available after approval"><span className="text-slate-300 text-[12px]">—</span></Tooltip>;
        // The reprint hits the piece-PRINT-gated sticker endpoint, so hide the
        // button (would only 403) for users without that permission.
        if (!mayPrintSticker) return <Tooltip title="Needs the piece-print permission"><span className="text-slate-300 text-[12px]">—</span></Tooltip>;
        return (
          <Tooltip title="View / print the remake piece's barcode + QR sticker">
            <Button size="small" icon={<QrcodeOutlined />} onClick={() => viewSticker(r.remakeBarcode)}>Sticker</Button>
          </Tooltip>
        );
      }
    }
  ], [viewSticker, mayPrintSticker]);

  const detailPending = detail && detail.status === 'PENDING';

  return (
    <ErpMasterShell>
      <div className="erp-production-page">
        {tabStrip}
        <ErpDataGrid
          title="Breakage Approvals"
          className="erp-brk-grid"
          gridId="production-breakage-approvals-v1"
          columns={columns}
          dataSource={rows}
          rowKey="id"
          loading={loading}
          rowHeight="compact"
          stickyHeader
          searchable
          searchPlaceholder="Search slip, WO, department…"
          searchableFields={['slipNo', 'woNos', 'departments', 'reasons']}
          exportable
          exportFilename="breakage-approvals"
          pageSize={20}
          pageSizeOptions={pageSizeOptions}
          onRefresh={refresh}
          onRowClick={openDetail}
          contextMenuActions={contextMenuActions}
          emptyTitle="Nothing here yet"
          emptyText={emptyText}
        />
      </div>

      <Drawer
        open={drawerOpen}
        onClose={closeDetail}
        width={820}
        destroyOnClose
        className="erp-brk-drawer"
        title={detail ? (
          <div className="flex items-center gap-2">
            <SafetyCertificateOutlined className="text-slate-400" />
            <span className="text-[15px] font-semibold text-slate-800">Breakage Slip</span>
            <span className="text-slate-400 font-normal">· {detail.slipNo}</span>
            <Tag color={statusColor(detail.status)} className="erp-grid__tag !ml-1" style={{ marginInlineEnd: 0 }}>
              {(statusLabel(detail.status) || '').toUpperCase()}
            </Tag>
          </div>
        ) : 'Breakage Slip'}
        extra={detail ? (
          <Space size={8}>
            {detail.status === 'APPROVED' && mayPrintSticker ? (
              <Tooltip title="Print every remake piece's barcode + QR sticker for this slip in one sheet">
                <Button size="small" type="primary" ghost icon={<QrcodeOutlined />} onClick={() => viewAllRemakeStickers(detail)}>
                  Print all remake stickers
                </Button>
              </Tooltip>
            ) : null}
            <Button size="small" icon={<FileTextOutlined />} onClick={() => viewSlip(detail)}>View slip</Button>
          </Space>
        ) : null}
        footer={detail && detailPending && mayDecide ? (
          <div className="flex justify-end gap-2">
            <Button danger icon={<CloseCircleOutlined />} disabled={approvingId === detail.id} onClick={() => reject(detail)}>Reject</Button>
            <Button type="primary" icon={<CheckCircleOutlined />} loading={approvingId === detail.id} onClick={() => approve(detail)}>Approve</Button>
          </div>
        ) : null}
      >
        {detail ? (
          <>
            <Descriptions size="small" column={2} bordered className="erp-production-meta"
              items={[
                { key: 'pieces', label: 'Broken pieces', children: <span className="tabular-nums font-semibold text-slate-700">{detail.pieceCount || (detail.items && detail.items.length) || 0}</span> },
                { key: 'requestedAt', label: 'Requested at', children: dateTime(detail.requestedAt) },
                { key: 'requestedBy', label: 'Requested by', children: detail.requestedByName || detail.requestedBy || '—' },
                { key: 'remarks', label: 'Remarks', children: detail.remarks || '—' },
                ...(detail.status === 'APPROVED' ? [
                  { key: 'approvedBy', label: 'Approved by', children: detail.approvedByName || detail.approvedBy || '—' },
                  { key: 'approvedAt', label: 'Approved at', children: dateTime(detail.approvedAt) }
                ] : []),
                ...(detail.status === 'REJECTED' ? [
                  { key: 'rejectedBy', label: 'Rejected by', children: detail.rejectedByName || detail.rejectedBy || '—' },
                  { key: 'rejectedAt', label: 'Rejected at', children: dateTime(detail.rejectedAt) },
                  { key: 'rejectionReason', label: 'Rejection reason', span: 2, children: detail.rejectionReason || '—' }
                ] : [])
              ]}
            />

            <div className="erp-production-section-title">Broken pieces</div>
            <Table
              size="small"
              rowKey={(r) => r.id || r.barcode}
              loading={detailBusy}
              columns={itemColumns}
              dataSource={Array.isArray(detail.items) ? detail.items : []}
              scroll={{ x: 1704 }}
              pagination={false}
              locale={{ emptyText: <Empty description="No pieces on this slip" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            />
          </>
        ) : null}
      </Drawer>

      {ApprovalMfaModalSlot}

      <ReportViewerModal
        open={report.open}
        title={report.title}
        html={report.html}
        loading={report.loading}
        error={report.error}
        width={report.width}
        onReload={reloadReport}
        onClose={closeReport}
      />
    </ErpMasterShell>
  );
}
